import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import { Menu } from "@headlessui/react"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { Pencil, Plus, Printer, Trash } from "components/icons/outline"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import { deleteFakturUmum, printFakturUmumUrl, printSuratJalanUmumUrl } from "./api"

// 290726 (permintaan user): dua tombol cetak DILEBUR jadi satu tombol "Print"
// ber-dropdown — pola sama dgn PrintActions di list Order HO. Label & keterangan
// tetap ditulis eksplisit karena keduanya sama-sama "cetak" tapi menghasilkan
// dokumen berbeda (surat jalan TANPA harga).
function PrintActions({ row }) {
  const items = [
    { label: "Faktur", description: "Dengan harga", href: printFakturUmumUrl(row.id) },
    { label: "Surat Jalan", description: "Tanpa harga", href: printSuratJalanUmumUrl(row.id) },
  ]

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex items-center gap-2 border border-neutral-200 bg-white text-neutral-800 shadow-sm transition hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200 px-3 py-2 text-[11px] rounded-lg">
        <Printer className="w-4 h-4" strokeWidth={1.5} />
        <span>Print</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-3.5 h-3.5 text-neutral-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </Menu.Button>

      <Menu.Items className="absolute right-0 z-30 mt-2 w-56 origin-top-right overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5 focus:outline-none">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 bg-neutral-50">
          Cetak Dokumen
        </div>
        <div className="p-1">
          {items.map((item) => (
            <Menu.Item key={item.href}>
              {({ active }) => (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition ${
                    active ? "bg-neutral-100" : "bg-white"
                  }`}
                >
                  <span className="text-xs font-semibold text-neutral-800">{item.label}</span>
                  <span className="text-[10px] text-neutral-500">{item.description}</span>
                </a>
              )}
            </Menu.Item>
          ))}
        </div>
      </Menu.Items>
    </Menu>
  )
}

function formatIdr(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("id-ID", { maximumFractionDigits: 0 })
}

export default function List() {
  const { user } = useAuth()
  const { setTitle } = useOutletContext()
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const page = searchParams.get("page") || 1
  const searchParam = searchParams.get("search") || ""
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""

  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)

  useEffect(() => setTitle("Faktur Umum"), [setTitle])
  useEffect(() => setSearch(searchParam), [searchParam])

  const { data: result_set, isLoading, mutate } = useResultSet(
    process.env.REACT_APP_INVENTORY_API_URL,
    "faktur-umum",
    {
      page,
      record_per_page: 10,
      search: searchParam || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const list = useMemo(() => (Array.isArray(result_set?.data) ? result_set.data : []), [result_set])
  const pagination = result_set?.pagination
  const currentPage = Number(pagination?.page || page || 1)
  const lastPage = Number(pagination?.last_page || 1)
  const total = pagination?.total ?? result_set?.total ?? 0

  const patch = (obj) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(obj).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })
    if ("search" in obj || "date_from" in obj || "date_to" in obj) next.delete("page")
    setSearchParams(next)
  }

  const onDelete = async (row) => {
    if (!window.confirm(`Hapus Faktur ${row.doc_number}?`)) return
    try {
      await deleteFakturUmum(row.id, user?.name)
      toast.success("Dokumen dihapus")
      mutate()
    } catch (err) {
      toast.error(err?.message || "Gagal menghapus")
    }
  }

  return (
    <Transition type="fade">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") patch({ search, page: 1 })
              }}
              placeholder="Cari no. faktur / penerbit / pelanggan"
              className="px-4 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
            />
            <div className="inline-flex items-center gap-2">
              <label className="text-xs text-neutral-500">Dari</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => patch({ date_from: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
              />
              <label className="text-xs text-neutral-500">s/d</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => patch({ date_to: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
              />
            </div>
          </div>

          <Link
            to="/faktur-umum/create"
            state={{ transition: "slide" }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-900 hover:bg-brand-800 rounded-xl"
          >
            <Plus className="w-4 h-4" stroke={1.5} />
            Tambah
          </Link>
        </div>

        <div className="overflow-x-auto bg-white border border-neutral-200 rounded-2xl">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">No. Faktur</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Tanggal</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Dari</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Kepada</th>
                <th className="px-4 py-3 text-xs font-medium text-right uppercase text-neutral-500">Total</th>
                <th className="px-4 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-sm text-center text-neutral-500">
                    {isLoading ? "Memuat..." : "Belum ada dokumen."}
                  </td>
                </tr>
              ) : (
                list.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-sm font-medium">{row.doc_number}</td>
                    <td className="px-4 py-3 text-sm">{row.doc_date}</td>
                    <td className="px-4 py-3 text-sm">{row.from_party || "-"}</td>
                    <td className="px-4 py-3 text-sm">{row.to_party || "-"}</td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      Rp {formatIdr(row.amount_total)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* 290726 (permintaan user): pola tombol DISAMAKAN dgn menu
                          Pembelian & Order HO ke Customer — di sana hanya EDIT yang
                          berupa tombol ikon bulat, sedangkan aksi lain memakai tombol
                          teks berwarna. Cetak Faktur vs Cetak Surat Jalan sengaja tetap
                          berlabel teks: keduanya sama-sama "cetak" tapi menghasilkan
                          dokumen berbeda (surat jalan tanpa harga), jadi ikon saja
                          rawan tertukar. */}
                      <div className="inline-flex items-center justify-end gap-2">
                        {/* 240726: SJU digabung ke faktur (cetak tanpa harga).
                            290726: keduanya dilebur ke dropdown Print. */}
                        <PrintActions row={row} />
                        <button
                          type="button"
                          onClick={() => navigate(`/faktur-umum/${row.id}/edit`, { state: { transition: "slide" } })}
                          title="Edit faktur"
                          aria-label="Edit faktur"
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 hover:bg-brand-600 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500"
                        >
                          <Trash className="w-4 h-4" strokeWidth={1.5} />
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600">Total {total} dokumen</p>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => patch({ page: currentPage - 1 })}
              className="px-3 py-1 text-sm border rounded-lg border-neutral-200 disabled:opacity-40"
            >
              Sebelumnya
            </button>
            <span className="text-sm text-neutral-600">
              {currentPage} / {lastPage}
            </span>
            <button
              type="button"
              disabled={currentPage >= lastPage}
              onClick={() => patch({ page: currentPage + 1 })}
              className="px-3 py-1 text-sm border rounded-lg border-neutral-200 disabled:opacity-40"
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>
    </Transition>
  )
}
